// Provider do step-up (`spec/17-frontend.md` §4.3). Registra o handler que
// `api/client.ts` chama quando a API devolve `403 mfa_required`; o dialog é montado
// uma única vez, aqui.

import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { setStepUpHandler } from '../api/client.ts';
import { sessionQueryOptions } from '../api/session.ts';
import { StepUpDialog } from '../components/app/step-up-dialog.tsx';
import { isAdminSurface } from './surface.ts';

interface Pending {
  resolve: (verified: boolean) => void;
}

const StepUpContext = createContext<(() => Promise<boolean>) | undefined>(undefined);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const session = useQuery(sessionQueryOptions);
  const [open, setOpen] = useState(false);
  const pending = useRef<Pending | undefined>(undefined);
  const twoFactorEnabled = session.data?.twoFactorEnabled === true;

  const requestStepUp = useCallback(async (): Promise<boolean> => {
    // AUTH-05: o seller sem 2FA confirma com a senha. No admin continua não havendo
    // step-up possível sem 2FA, o `ApiError` sobe e o roteador leva para `/admin/setup-2fa`.
    if (!twoFactorEnabled && isAdminSurface()) {
      return false;
    }
    return new Promise<boolean>((resolve) => {
      pending.current = { resolve };
      setOpen(true);
    });
  }, [twoFactorEnabled]);

  useEffect(() => {
    setStepUpHandler(requestStepUp);
    return () => {
      setStepUpHandler(undefined);
    };
  }, [requestStepUp]);

  const settle = useCallback((verified: boolean) => {
    setOpen(false);
    pending.current?.resolve(verified);
    pending.current = undefined;
  }, []);

  return (
    <StepUpContext value={requestStepUp}>
      {children}
      <StepUpDialog open={open} twoFactorEnabled={twoFactorEnabled} onDone={settle} />
    </StepUpContext>
  );
}

export function useStepUp(): () => Promise<boolean> {
  const value = use(StepUpContext);
  if (value === undefined) {
    throw new Error('useStepUp fora do StepUpProvider');
  }
  return value;
}
